/**
 * FE-024 — mobile bottom nav bar (Figma `Bottomnav bar` 11:39225 / symbol 11:39227).
 *
 * Geometry from the symbol: plate 393×68, `Rectangle 96` minus `Ellipse 200`
 * 124×124 at (135, −62) — a circle r=62 centred on the top edge. The AIRA ball
 * (`Frame 67`, 88×88) sits at y=−29.5 from the plate top, i.e. its centre is
 * 14.5px below the top edge. Icon buttons 46×44 at y=12, glyph 20.
 */

import { FAVORITES_ROUTE, HOME_ROUTE, LISTS_ROUTE, PROFILE_ROUTE, STYLE_SPACE_ROUTE } from '@/router/routes'
import type { FairIconName } from '@/ui/tokens/icons.data'

export const FAIR_BOTTOM_NAV_HEIGHT = 68
export const FAIR_BOTTOM_NAV_CUTOUT_RADIUS = 62
export const FAIR_BOTTOM_NAV_BALL_SIZE = 88
/** Distance from the plate's top edge down to the ball's centre. */
export const FAIR_BOTTOM_NAV_BALL_CENTER_OFFSET = 14.5

/** `<html>` class that turns `--fair-bottomnav-height` on for page paddings. */
export const FAIR_BOTTOM_NAV_ROOT_CLASS = 'fair-has-bottomnav'

/** Hide / show on scroll: ignore jitter below this many px … */
export const FAIR_BOTTOM_NAV_SCROLL_THRESHOLD = 8
/** … and always show while this close to the top. */
export const FAIR_BOTTOM_NAV_TOP_GUARD = 24
/** Same duration as the Home v2 header (`fair-home-v2.css`). */
export const FAIR_BOTTOM_NAV_MOTION_MS = 300

export type FairBottomNavTabId = 'home' | 'wardrobe' | 'saved' | 'profile'

/**
 * FE-017 — Material «emphasis» motion per glyph: the icon's own parts move,
 * staggered, when the tab becomes current (m2.material.io → Animated icons).
 * `settle` — the house lands with a small bounce; `swing` — the hanger swings
 * on its hook like a pendulum; `beat` — the heart beats twice and fills;
 * `nod` — the head bobs, the shoulders follow a beat later.
 */
export type FairBottomNavMotion = 'settle' | 'swing' | 'beat' | 'nod'

export interface FairBottomNavTab {
  id: FairBottomNavTabId
  icon: FairIconName
  /** Drawn on top of `icon` when the tab is current (heart outline → filled). */
  activeIcon?: FairIconName
  to: string
  /** Needs a signed-in user; otherwise the auth modal opens instead. */
  auth?: boolean
  /** i18n key under `fair_bottom_nav`. */
  labelKey: string
  motion: FairBottomNavMotion
  /** Subpath groups rendered as separate `<path>` parts (see `splitIconSvgParts`). */
  parts?: number[][]
}

/** Left pair, [AIRA], right pair — the symbol's order. */
export const FAIR_BOTTOM_NAV_TABS: readonly FairBottomNavTab[] = [
  { id: 'home', icon: 'home-outlined', to: HOME_ROUTE, labelKey: 'home', motion: 'settle' },
  { id: 'wardrobe', icon: 'hanger', to: STYLE_SPACE_ROUTE, labelKey: 'wardrobe', motion: 'swing' },
  { id: 'saved', icon: 'favorite-outlined', activeIcon: 'favorite-filled', to: FAVORITES_ROUTE, labelKey: 'saved', motion: 'beat' },
  // `account`: subpaths 0+3 are the head (outer + hole), 1+2 the shoulders.
  { id: 'profile', icon: 'account', to: PROFILE_ROUTE, auth: true, labelKey: 'profile', motion: 'nod', parts: [[0, 3], [1, 2]] },
]

export const FAIR_BOTTOM_NAV_START_TABS = FAIR_BOTTOM_NAV_TABS.slice(0, 2)
export const FAIR_BOTTOM_NAV_END_TABS = FAIR_BOTTOM_NAV_TABS.slice(2)

/** Which tab the current route lights up; `null` when none does. */
export function resolveFairBottomNavActive(path: string): FairBottomNavTabId | null {
  if (path === HOME_ROUTE || path === '/home') return 'home'
  if (path.startsWith(STYLE_SPACE_ROUTE)) return 'wardrobe'
  if (path.startsWith(FAVORITES_ROUTE) || path.startsWith(LISTS_ROUTE)) return 'saved'
  if (path.startsWith(PROFILE_ROUTE)) return 'profile'
  return null
}

export interface FairBottomNavScrollInput {
  scrollY: number
  lastY: number
  hidden: boolean
  threshold?: number
  topGuard?: number
}

/**
 * Scroll down past the guard → hide; scroll up, or come back near the top →
 * show. Movements under the threshold are jitter and change nothing.
 */
export function resolveFairBottomNavScrollIntent(input: FairBottomNavScrollInput): 'hide' | 'show' | null {
  const threshold = input.threshold ?? FAIR_BOTTOM_NAV_SCROLL_THRESHOLD
  const topGuard = input.topGuard ?? FAIR_BOTTOM_NAV_TOP_GUARD
  const y = Math.max(0, input.scrollY)
  if (y <= topGuard) return input.hidden ? 'show' : null
  const delta = y - input.lastY
  if (Math.abs(delta) < threshold) return null
  if (delta > 0) return input.hidden ? null : 'hide'
  return input.hidden ? 'show' : null
}
